import * as XLSX from 'xlsx';

const safe = (value = '') => String(value ?? '').trim();

function first(row = {}, keys = [], fallback = '') {
  for (const key of keys) {
    const val = row?.[key];
    if (safe(val)) return val;
  }
  return fallback;
}

function asYmd(value) {
  const raw = safe(value);
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const dmy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? raw : parsed.toISOString().slice(0, 10);
}

export const EMP_MASTER_COLUMNS = [
  // 1. Core Profile & Role
  { header: 'Employee ID', width: 15, get: (r) => first(r, ['EMP Code', 'Employee ID', 'User ID', 'empCode']) },
  { header: 'Employee Name', width: 25, get: (r) => first(r, ['Name', 'Employee Name', 'name']) },
  { header: 'Category', width: 14, get: (r) => r.Category || r._sourceSheet || 'EMP' },
  { header: 'Role', width: 16, get: (r) => first(r, ['Role', 'Designation'], 'User') },
  { header: 'Designation', width: 24, get: (r) => first(r, ['Designation', 'Role'], '-') },
  { header: 'Department', width: 20, get: (r) => r.Department || '-' },
  { header: 'Status', width: 13, get: (r) => r.Status || 'Active' },
  { header: 'User ID / Login ID', width: 18, get: (r) => first(r, ['User ID', 'Employee ID', 'EMP Code']) },

  // 2. Reporting & Organization
  { header: 'Reporting Manager ID', width: 22, get: (r) => first(r, ['Manager ID', 'Manager', 'Reporting to'], '-') },
  { header: 'Task Approver ID', width: 22, get: (r) => first(r, ['Task Approver', 'Approver'], '-') },
  { header: 'Assign Buddy ID', width: 20, get: (r) => first(r, ['Assign Buddy', 'Buddy'], '-') },
  { header: 'Company Name', width: 25, get: (r) => r['Company Name'] || 'Kriscel Tech Pvt. Ltd.' },
  { header: 'Company Code', width: 14, get: (r) => r['Company Code'] || 'KRIS' },

  // 3. Work Schedule & Dates
  { header: 'Date of Joining', width: 16, get: (r) => asYmd(first(r, ['Date of Joining', 'Joining Date'])) },
  { header: 'Date of Exit (DOE)', width: 16, get: (r) => asYmd(r.DOE) },
  { header: 'Job Type', width: 16, get: (r) => r['Job Type'] || '-' },
  { header: 'Working Hours', width: 14, get: (r) => r['Working Hours'] || '-' },
  { header: 'In Timing', width: 14, get: (r) => r['In Timing'] || '-' },
  { header: 'Week Off', width: 16, get: (r) => r['Week Off'] || '-' },
  { header: 'Probation Period', width: 16, get: (r) => r['PROBATION PERIOD'] || '-' },

  // 4. Contact Information
  { header: 'Mobile Number', width: 16, get: (r) => first(r, ['Phone No', 'Mobile Number', 'Mobile'], '-') },
  { header: 'Official Email ID', width: 28, get: (r) => first(r, ['Official mail id if any', 'Email'], '-') },
  { header: 'Personal Email ID', width: 28, get: (r) => first(r, ['Personal Email ID'], '-') },

  // 5. Personal Details
  { header: 'Gender', width: 12, get: (r) => r.Gender || '-' },
  { header: 'Marital Status', width: 14, get: (r) => r['Marital Status'] || '-' },
  { header: 'Date of Birth', width: 15, get: (r) => asYmd(r['Date of Birth']) },
  { header: 'Blood Group', width: 12, get: (r) => r['Blood Group'] || '-' },
  { header: 'Father Name', width: 22, get: (r) => r['Father Name'] || '-' },
  { header: 'Mother Name', width: 22, get: (r) => r['Mother Name'] || '-' },
  { header: 'Spouse Name', width: 22, get: (r) => r['Spouse Name'] || '-' },
  { header: 'Kids', width: 10, get: (r) => r.Kids || '-' },
  { header: 'Anniversary Date', width: 16, get: (r) => asYmd(r['Anniversary Date']) },
  { header: 'Current Address', width: 36, get: (r) => r['Current Address'] || '-' },
  { header: 'Permanent Address', width: 36, get: (r) => r['Permanent Address'] || '-' },
  { header: 'Emergency Contact No', width: 20, get: (r) => r['Emergency contact No.'] || '-' },
  { header: 'Emergency Contact Person', width: 24, get: (r) => r['Emergency Person Name'] || '-' },

  // 6. Bank & Statutory
  { header: 'Bank Name', width: 22, get: (r) => r['Bank Name'] || '-' },
  { header: 'Bank Account No', width: 22, get: (r) => r['Bank Account No'] ? String(r['Bank Account No']) : '-' },
  { header: 'Bank IFSC Code', width: 16, get: (r) => r['Bank IFSC Code'] || '-' },
  { header: 'Take Home Salary', width: 18, get: (r) => r['Salary (Take Home)'] || '-' },
  { header: 'CTC', width: 16, get: (r) => r.CTC || '-' },
  { header: 'PAN Card No', width: 16, get: (r) => r['Pan Card No'] || '-' },
  { header: 'Aadhaar No', width: 18, get: (r) => r['Aadhar No'] ? String(r['Aadhar No']) : '-' },
  { header: 'PF No / Status', width: 16, get: (r) => r.PF || '-' },
  { header: 'ESIC No / Status', width: 16, get: (r) => r.ESIC || '-' },

  // 7. Documents Link Summary
  { header: 'Offer Letter Link', width: 35, get: (r) => r['OFFER LETTER LINK'] || '-' },
  { header: 'Appointment Letter Link', width: 35, get: (r) => r['APPOINTMENT LETTER LINK'] || '-' },
  { header: 'Aadhaar Card Link', width: 35, get: (r) => r['AADHAAR CARD LINK'] || '-' },
  { header: 'PAN Card Link', width: 35, get: (r) => r['PAN CARD LINK'] || '-' },
  { header: 'Bank Proof Link', width: 35, get: (r) => r['BANK PROOF LINK'] || '-' },
  { header: 'Education Cert Link', width: 35, get: (r) => r['EDUCATION CERTIFICATE LINK'] || '-' },
  { header: 'Other Documents', width: 35, get: (r) => r['Documents of Employee'] || '-' }
];

export const DOCUMENTS_EXPORT_COLUMNS = [
  { header: 'Employee ID', width: 15, get: (r) => first(r, ['EMP Code', 'Employee ID', 'User ID', 'empCode']) },
  { header: 'Employee Name', width: 25, get: (r) => first(r, ['Name', 'Employee Name', 'name']) },
  { header: 'Category', width: 14, get: (r) => r.Category || r._sourceSheet || 'EMP' },
  { header: 'Department', width: 20, get: (r) => r.Department || '-' },
  { header: 'Offer Letter', width: 35, get: (r) => r['OFFER LETTER LINK'] || '-' },
  { header: 'Appointment Letter', width: 35, get: (r) => r['APPOINTMENT LETTER LINK'] || '-' },
  { header: 'Aadhaar Card', width: 35, get: (r) => r['AADHAAR CARD LINK'] || '-' },
  { header: 'PAN Card', width: 35, get: (r) => r['PAN CARD LINK'] || '-' },
  { header: 'Bank Proof', width: 35, get: (r) => r['BANK PROOF LINK'] || '-' },
  { header: 'Education Certificate', width: 35, get: (r) => r['EDUCATION CERTIFICATE LINK'] || '-' },
  { header: 'Other Documents', width: 35, get: (r) => r['Documents of Employee'] || '-' }
];

/**
 * Builds a formatted SheetJS worksheet from rows and column definitions.
 */
export function buildWorksheet(rows = [], columns = EMP_MASTER_COLUMNS) {
  const headers = columns.map((col) => col.header);
  const data = rows.map((row) => {
    const item = {};
    columns.forEach((col) => {
      const val = col.get ? col.get(row) : (row[col.header] ?? '');
      item[col.header] = val ?? '';
    });
    return item;
  });

  const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });

  // Calculate auto column widths
  worksheet['!cols'] = columns.map((col) => {
    let maxLen = col.header.length;
    for (let i = 0; i < Math.min(rows.length, 100); i++) {
      const val = String(col.get ? col.get(rows[i]) : rows[i]?.[col.header] || '');
      if (val.length > maxLen) {
        maxLen = val.length;
      }
    }
    const width = Math.min(Math.max(col.width || 14, maxLen + 2), 50);
    return { wch: width };
  });

  return worksheet;
}

/**
 * Triggers browser download for a workbook.
 */
export function triggerDownload(workbook, fileName) {
  XLSX.writeFile(workbook, fileName);
}

/**
 * Export current view (filtered or category rows)
 */
export function exportCurrentView(rows = [], categoryName = 'Master', search = '') {
  const isDocView = categoryName === 'Documents';
  const cols = isDocView ? DOCUMENTS_EXPORT_COLUMNS : EMP_MASTER_COLUMNS;
  const worksheet = buildWorksheet(rows, cols);

  const workbook = XLSX.utils.book_new();
  const safeSheetName = (categoryName || 'Master').slice(0, 31);
  XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);

  const dateStr = new Date().toISOString().slice(0, 10);
  const filterTag = search ? '_filtered' : '';
  const fileName = `EMP_Master_${categoryName.replace(/\s+/g, '_')}${filterTag}_${dateStr}.xlsx`;

  triggerDownload(workbook, fileName);
  return { success: true, count: rows.length, fileName };
}

/**
 * Export all master rows as single master sheet
 */
export function exportAllMaster(rows = []) {
  const worksheet = buildWorksheet(rows, EMP_MASTER_COLUMNS);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'All Employees');

  const dateStr = new Date().toISOString().slice(0, 10);
  const fileName = `EMP_Master_All_Employees_${dateStr}.xlsx`;

  triggerDownload(workbook, fileName);
  return { success: true, count: rows.length, fileName };
}

/**
 * Export complete multi-sheet workbook
 */
export function exportMultiSheetWorkbook({
  allRows = [],
  empRows = [],
  freelancerRows = [],
  internRows = [],
  inactiveRows = [],
  documentRows = []
}) {
  const workbook = XLSX.utils.book_new();

  // 1. All Master Data
  if (allRows.length) {
    XLSX.utils.book_append_sheet(workbook, buildWorksheet(allRows, EMP_MASTER_COLUMNS), 'All Active');
  }

  // 2. Employees (EMP)
  if (empRows.length) {
    XLSX.utils.book_append_sheet(workbook, buildWorksheet(empRows, EMP_MASTER_COLUMNS), 'Employees');
  }

  // 3. Freelancers
  if (freelancerRows.length) {
    XLSX.utils.book_append_sheet(workbook, buildWorksheet(freelancerRows, EMP_MASTER_COLUMNS), 'Freelancers');
  }

  // 4. Interns
  if (internRows.length) {
    XLSX.utils.book_append_sheet(workbook, buildWorksheet(internRows, EMP_MASTER_COLUMNS), 'Interns');
  }

  // 5. Inactive Users
  if (inactiveRows.length) {
    XLSX.utils.book_append_sheet(workbook, buildWorksheet(inactiveRows, EMP_MASTER_COLUMNS), 'Inactive');
  }

  // 6. Documents
  if (documentRows.length) {
    XLSX.utils.book_append_sheet(workbook, buildWorksheet(documentRows, DOCUMENTS_EXPORT_COLUMNS), 'Documents');
  }

  const dateStr = new Date().toISOString().slice(0, 10);
  const fileName = `EMP_Master_Complete_Workbook_${dateStr}.xlsx`;

  triggerDownload(workbook, fileName);
  const totalCount = allRows.length + inactiveRows.length;
  return { success: true, count: totalCount, fileName };
}
